import React, { Component } from 'react';
import { AlertTriangle, RotateCcw, Bug, ArrowRight } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { BaseErrorBoundary, ErrorFallbackProps } from '../../components/shared/BaseErrorBoundary';
import { DjayNarrator } from '../../mascot';
/**
 * QuestionErrorBoundary — Catches rendering errors in question renderers.
 *
 * Prevents renderer crashes from breaking the entire practice session.
 * Shows a friendly fallback UI with skip/reset functionality.
 * Includes dev-only debug panel with question ID, error, and stack trace.
 * Now uses BaseErrorBoundary for shared lifecycle logic (s106 P2-18).
 *
 * Co-located in practice/ domain (CL-1 s63).
 */
// ── Types ────────────────────────────────────────────────────
interface QuestionErrorBoundaryProps {
  children: React.ReactNode;
  questionId: string;
  onReset: () => void;
  onSkipQuestion?: () => void;
}
// ── Component ────────────────────────────────────────────────
export class QuestionErrorBoundary extends Component<QuestionErrorBoundaryProps> {
  renderFallback = ({
    error,
    errorInfo,
    onReset
  }: ErrorFallbackProps): React.ReactNode => {
    return <div role="alert" aria-live="assertive" className="w-full max-w-2xl mx-auto px-4 py-8" data-id="element-11080">
        <Card className="p-8 border-2 border-amber-200 bg-amber-50/50 shadow-xl shadow-amber-100" data-id="element-11081">
          {/* Header with Djay */}
          <div className="flex items-start gap-4 mb-6" data-id="element-11082">
            <DjayNarrator emotion="confused" size="lg" className="flex-shrink-0" data-id="element-11083" />
            <div className="flex-1" data-id="element-11084">
              <div className="flex items-center gap-2 mb-2" data-id="element-11085">
                <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-11086" />
                <h3 className="text-xl font-bold text-slate-900" data-id="element-11087">
                  Deze vraag kon niet worden geladen
                </h3>
              </div>
              <p className="text-slate-700 leading-relaxed" data-id="element-11088">
                Er ging iets mis bij het tonen van deze vraag. Je kunt de vraag
                overslaan of opnieuw proberen.
              </p>
            </div>
          </div>

          {/* Dev Debug Panel */}
          {import.meta.env?.DEV && error && <div className="mb-6 p-4 bg-rose-50 rounded-lg border border-rose-200" data-id="element-11089">
              <div className="flex items-center gap-2 mb-2" data-id="element-11090">
                <Bug className="w-4 h-4 text-rose-600" data-id="element-11091" />
                <p className="text-xs font-bold text-rose-800" data-id="element-11092">
                  Debug Info (dev only)
                </p>
              </div>
              <div className="space-y-2 text-xs font-mono text-rose-700" data-id="element-11093">
                <div data-id="element-11094">
                  <span className="font-bold" data-id="element-11095">Question ID:</span>{' '}
                  {this.props.questionId}
                </div>
                <div data-id="element-11096">
                  <span className="font-bold" data-id="element-11097">Error:</span> {error.message}
                </div>
                {error.stack && <div className="mt-2 p-2 bg-rose-100 rounded overflow-x-auto" data-id="element-11098">
                    <pre className="text-[10px] whitespace-pre-wrap break-all" data-id="element-11099">
                      {error.stack}
                    </pre>
                  </div>}
                {errorInfo?.componentStack && <div className="mt-2 p-2 bg-rose-100 rounded overflow-x-auto" data-id="element-11100">
                    <div className="font-bold mb-1" data-id="element-11101">Component Stack:</div>
                    <pre className="text-[10px] whitespace-pre-wrap" data-id="element-11102">
                      {errorInfo.componentStack}
                    </pre>
                  </div>}
              </div>
            </div>}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-11103">
            {this.props.onSkipQuestion && <Button onClick={() => {
            onReset();
            this.props.onSkipQuestion?.();
          }} variant="primary" size="xl" fullWidth className="shadow-lg shadow-indigo-200" data-id="element-11104">
                <ArrowRight className="w-5 h-5 mr-2" data-id="element-11105" />
                Sla deze vraag over
              </Button>}
            <Button onClick={onReset} variant="secondary" size="xl" fullWidth data-id="element-11106">
              <RotateCcw className="w-5 h-5 mr-2" data-id="element-11107" />
              Probeer opnieuw
            </Button>
          </div>
        </Card>
      </div>;
  };
  render(): React.ReactNode {
    return <BaseErrorBoundary logPrefix="Question Renderer" logContext={{
      questionId: this.props.questionId
    }} resetKey={this.props.questionId} fallback={this.renderFallback} onReset={this.props.onReset} respectStrictMode data-id="element-11108">
        {this.props.children}
      </BaseErrorBoundary>;
  }
}